import React from "react";
import { ShoppingBag, Menu, X, Play, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { KNOW_IT_OWLS_LOGO } from "@/data/products";
import { motion } from "framer-motion";

// Header nav → section IDs on the landing page. "Trailer" jumps to the
// Class in Action / Watch on Tubi section since that's where the show
// content actually lives.
const TUBI_SHOW_URL = "https://tubitv.com/series/300021533/substitute-teacher";

const NAV = [
  { label: "Home", id: "hero" },
  { label: "Shop", id: "shop" },
  { label: "Cast", id: "cast" },
  { label: "Trailer", id: "class-in-action" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { customer } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Smooth-scroll to a section. If we're not on the landing page (e.g. /track,
  // /faq), route to `/#<id>` first and let Landing.jsx pick up the hash on
  // mount and finish the scroll.
  const goToSection = (id) => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="site-header-blur sticky top-0 z-40 border-b-2 border-[#111] bg-[var(--st-bg)]/70"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
        <button
          onClick={() => goToSection("hero")}
          className="flex items-center gap-3"
          data-testid="logo-button"
        >
          <img
            src={KNOW_IT_OWLS_LOGO}
            alt="The Know It Owls"
            className="h-10 sm:h-12 md:h-14 w-auto"
          />
          <div className="flex flex-col items-start">
            <span className="font-display text-sm sm:text-lg md:text-xl tracking-tight text-[var(--st-maroon)] leading-[0.95]">
              SUBSTITUTE
            </span>
            <span className="font-display text-sm sm:text-lg md:text-xl tracking-tight text-[var(--st-mustard)] leading-[0.95]">
              TEACHER
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => goToSection(n.id)}
              className="font-heading uppercase tracking-[0.18em] text-[1.25em] text-[var(--st-fg)] hover:text-[var(--st-orange)] transition-colors"
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* WATCH the show — deep-link straight to the Tubi series page */}
          <a
            href={TUBI_SHOW_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="header-watch-show"
            className="hidden sm:inline-flex items-center gap-1.5 border-2 border-[#111] bg-[var(--st-maroon)] text-white px-3 py-2 font-heading uppercase tracking-[0.18em] text-sm shadow-brutal-sm hover:bg-[var(--st-orange)] hover:text-[var(--st-fg)] transition-colors"
          >
            <Play size={14} fill="currentColor" />
            <span className="hidden md:inline">Watch the Show</span>
            <span className="md:hidden">Watch</span>
          </a>

          <Link
            to={customer ? "/account" : "/account/login"}
            data-testid="header-account"
            title={customer ? `Signed in as ${customer.email}` : "Sign in"}
            className="hidden sm:inline-flex items-center gap-1.5 border-2 border-[#111] bg-white px-3 py-2 font-heading uppercase tracking-[0.18em] text-sm shadow-brutal-sm hover:bg-[var(--st-mustard)] transition-colors"
          >
            <User size={14} />
            <span className="hidden md:inline">
              {customer ? customer.first_name || "Account" : "Sign in"}
            </span>
          </Link>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setOpen(true)}
            data-testid="open-cart-button"
            className="relative flex items-center gap-2 border-2 border-[#111] bg-[var(--st-gold)] px-3 py-2 font-heading uppercase tracking-[0.18em] text-sm shadow-brutal-sm hover:bg-[var(--st-orange)] transition-colors"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline">Cart</span>
            <span
              data-testid="cart-count"
              className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-[var(--st-maroon)] text-white text-[11px] leading-none rounded-full"
            >
              {count}
            </span>
          </motion.button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden border-2 border-[#111] bg-white p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t-2 border-[#111] bg-[var(--st-bg)]">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => goToSection(n.id)}
                className="text-left font-heading uppercase tracking-[0.18em] text-base py-2 border-b border-dashed border-[#11111122]"
                data-testid={`mobile-nav-${n.id}`}
              >
                {n.label}
              </button>
            ))}
            <a
              href={TUBI_SHOW_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-watch-show"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-4 py-3 font-heading uppercase tracking-[0.18em] text-sm shadow-brutal-sm"
            >
              <Play size={14} fill="currentColor" /> Watch the Show on Tubi
            </a>
            <Link
              to={customer ? "/account" : "/account/login"}
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-account"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#111] px-4 py-3 font-heading uppercase tracking-[0.18em] text-sm shadow-brutal-sm"
            >
              <User size={14} />{" "}
              {customer ? customer.first_name || "My Account" : "Sign in"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
